import { useEffect, useState } from 'react';

import { trpc, TrpcInputs, TrpcOutputs } from '../config/trpc';

type TRPCFunc<T extends keyof TrpcInputs> = (args: TrpcInputs[T]) => Promise<TrpcOutputs[T]>;

type UseEmail = {
  isLoading: boolean;
  errors?: string[];
  sendAirlineQuotingEmail: TRPCFunc<'sendAirlineQuotingEmail'>;
  sendTruckingQuotingEmail: TRPCFunc<'sendTruckingQuotingEmail'>;
  sendWarehouseQuotingEmail: TRPCFunc<'sendWarehouseQuotingEmail'>;
  sendAirlineBookingEmail: TRPCFunc<'sendAirlineBookingEmail'>;
  sendAirlineBookingChangeEmail: TRPCFunc<'sendAirlineBookingChangeEmail'>;
  sendAirlineBookingCancelEmail: TRPCFunc<'sendAirlineBookingCancelEmail'>;
  sendAirlineRequestScheduleEmail: TRPCFunc<'sendAirlineRequestScheduleEmail'>;
  sendQuoteUpdateNotification: TRPCFunc<'sendRequestClarificationEmail'>;
  sendRequestClarificationNotification: TRPCFunc<'sendRequestClarificationEmail'>;
  sendBookingConfirmNotification: TRPCFunc<'sendBookingConfirmEmail'>;
  sendNewRateEmail: TRPCFunc<'sendNewRateEmail'>;
  sendGenericEmailToFF: TRPCFunc<'sendGenericEmailToFF'>;
};

export const useEmail = () => {
  const [errors, setErrors] = useState<string[]>([]);
  const sendAirlineQuotingEmail = trpc.sendAirlineQuotingEmail.useMutation();
  const sendTruckingQuotingEmail = trpc.sendTruckingQuotingEmail.useMutation();
  const sendWarehouseQuotingEmail = trpc.sendWarehouseQuotingEmail.useMutation();
  const sendAirlineBookingEmail = trpc.sendAirlineBookingEmail.useMutation();
  const sendAirlineBookingChangeEmail = trpc.sendAirlineBookingChangeEmail.useMutation();
  const sendAirlineBookingCancelEmail = trpc.sendAirlineBookingCancelEmail.useMutation();
  const sendAirlineRequestScheduleEmail = trpc.sendAirlineRequestScheduleEmail.useMutation();
  const quoteUpdate = trpc.sendQuoteUpdateEmail.useMutation();
  const requestClarification = trpc.sendRequestClarificationEmail.useMutation();
  const bookingConfirm = trpc.sendBookingConfirmEmail.useMutation();
  const sendNewRateEmail = trpc.sendNewRateEmail.useMutation();
  const sendGenericEmailToFF = trpc.sendGenericEmailToFF.useMutation();

  useEffect(() => {
    const nextErrors: string[] = [];
    if (sendAirlineQuotingEmail.error) nextErrors?.push(sendAirlineQuotingEmail.error?.message);
    if (sendTruckingQuotingEmail.error) nextErrors?.push(sendTruckingQuotingEmail.error?.message);
    if (sendWarehouseQuotingEmail.error) nextErrors?.push(sendWarehouseQuotingEmail.error?.message);
    if (sendAirlineBookingEmail.error) nextErrors?.push(sendAirlineBookingEmail.error?.message);
    if (sendAirlineBookingChangeEmail.error) nextErrors?.push(sendAirlineBookingChangeEmail.error?.message);
    if (sendAirlineRequestScheduleEmail.error) nextErrors?.push(sendAirlineRequestScheduleEmail.error?.message);
    if (quoteUpdate.error) nextErrors?.push(quoteUpdate.error?.message);
    if (requestClarification.error) nextErrors?.push(requestClarification.error?.message);
    if (bookingConfirm.error) nextErrors?.push(bookingConfirm.error?.message);
    if (sendGenericEmailToFF.error) nextErrors?.push(sendGenericEmailToFF.error?.message);
    if (nextErrors.length > 0) setErrors(nextErrors);
  }, [
    quoteUpdate.error,
    requestClarification.error,
    sendAirlineQuotingEmail.error,
    sendAirlineBookingEmail.error,
    sendAirlineBookingChangeEmail.error,
    bookingConfirm.error,
    sendNewRateEmail.error,
    sendAirlineRequestScheduleEmail.error,
    sendTruckingQuotingEmail.error,
    sendWarehouseQuotingEmail.error,
    sendGenericEmailToFF.error,
  ]);

  const isLoading =
    quoteUpdate.isLoading ||
    requestClarification.isLoading ||
    sendAirlineQuotingEmail.isLoading ||
    sendAirlineBookingEmail.isLoading ||
    sendAirlineBookingChangeEmail.isLoading ||
    bookingConfirm.isLoading ||
    sendNewRateEmail.isLoading ||
    sendTruckingQuotingEmail.isLoading ||
    sendWarehouseQuotingEmail.isLoading ||
    sendGenericEmailToFF.isLoading;

  type AsyncFunc = (...args) => Promise<any>;

  const captureWrap = <T extends AsyncFunc>(func: T) => {
    return async (...args: Parameters<T>) => {
      const result = await func(...args);
      if (args?.[0]?.overrides?.clipboard) {
        const content = new Blob([result], { type: 'text/html' });
        const clipitem = new ClipboardItem({ 'text/html': content });
        navigator.clipboard.write([clipitem]);
      }
      return result;
    };
  };

  return {
    isLoading,
    errors,
    sendQuoteUpdateNotification: captureWrap(quoteUpdate.mutateAsync),
    sendRequestClarificationNotification: captureWrap(requestClarification.mutateAsync),
    sendAirlineQuotingEmail: captureWrap(sendAirlineQuotingEmail.mutateAsync),
    sendTruckingQuotingEmail: captureWrap(sendTruckingQuotingEmail.mutateAsync),
    sendAirlineBookingEmail: captureWrap(sendAirlineBookingEmail.mutateAsync),
    sendAirlineBookingChangeEmail: captureWrap(sendAirlineBookingChangeEmail.mutateAsync),
    sendAirlineBookingCancelEmail: captureWrap(sendAirlineBookingCancelEmail.mutateAsync),
    sendAirlineRequestScheduleEmail: captureWrap(sendAirlineRequestScheduleEmail.mutateAsync),
    sendBookingConfirmNotification: captureWrap(bookingConfirm.mutateAsync),
    sendNewRateEmail: captureWrap(sendNewRateEmail.mutateAsync),
    sendWarehouseQuotingEmail: captureWrap(sendWarehouseQuotingEmail.mutateAsync),
    sendGenericEmailToFF: captureWrap(sendGenericEmailToFF.mutateAsync),
  } satisfies UseEmail;
};
